import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { SrOnly } from '@/components/Layout/Parts';
import { Modal } from '@/components/Modal';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

import BlacklistForm from './BlacklistForm';
import BlacklistTableRow from './BlacklistTableRow';

const Dialog = styled(StyledDialog)`
  width: 1160px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px 24px 0;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  height: 614px;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const TableWrap = styled.div`
  max-height: calc(100vh - 400px);
  overflow-y: auto;
  position: relative;
  ${scrollBarStyles};

  > table {
    border-collapse: separate;
  }

  th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  }

  td {
    font-weight: 700;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`;

const StyledText = styled(Text)`
  margin-bottom: 16px;
`;

export interface BlacklistRow {
  id: number;
  domain: string;
}

interface Props {
  onClose: VoidFunction;
  whitelist: boolean;
}

const BlacklistModal: FC<Props> = ({ onClose, whitelist }) => {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  const { data, isLoading, isError } = useQuery(
    [
      'dq_comment_links_blacklist',
      {
        ...(!!debouncedSearch && { search: debouncedSearch }),
        whitelist,
      },
    ],
    () =>
      api.get<never, BlacklistRow[]>('/v1/editorial/malicious-comment-links', {
        params: {
          ...(!!debouncedSearch && { search: debouncedSearch }),
          is_whitelist: whitelist ? 1 : 0,
        },
      }),
  );

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">{whitelist ? 'Whitelist' : 'Blacklist'}</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          {!whitelist && (
            <StyledText $type="captionSemibold">
              If any of the provided phrases will be detected within comment -
              it will be deleted automatically without approval.
            </StyledText>
          )}
          <SearchBar
            placeholder="Search by phrase"
            value={search}
            setValue={setSearch}
          />
          <TableWrap>
            <Table $fixed>
              <thead>
                <tr>
                  <Th>Phrase</Th>
                  <Th $actionsCol>
                    <SrOnly>Actions</SrOnly>
                  </Th>
                </tr>
              </thead>
              <tbody>
                <TableContent
                  isLoading={isLoading}
                  isError={isError}
                  colSpan={2}
                  hasData={!!data?.length}
                >
                  {data?.map((row) => (
                    <BlacklistTableRow row={row} key={row.id} />
                  ))}
                </TableContent>
              </tbody>
            </Table>
          </TableWrap>
        </Body>
        <Footer>
          <Button type="button" onClick={() => setShowForm(true)}>
            Add new phrase
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
      <AnimatePresence>
        {showForm && (
          <BlacklistForm
            onClose={() => setShowForm(false)}
            whitelist={whitelist}
          />
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default BlacklistModal;
